import React, { useEffect, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CContainer,
  CSpinner,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CButton,
  CProgress,
  CProgressBar,
} from "@coreui/react";
import { CChartBar } from "@coreui/react-chartjs";
import settingsStore from "../../store/SettingsStore";
import groupsStore from "../../store/GroupsStore";
import PeriodFilters from "../UI/PeriodFilters/PeriodFilters";
import ServerSelect from "../UI/PeriodFilters/ServerSelect";
import { fmtInt } from "../../utils/chartHelpers";
import { formatYmd } from "../../utils/dateRange";
import { logReportView } from "../../api/reportLogApi";

const GroupsPage = observer(() => {
  const navigate = useNavigate();
  const [selectedServer, setSelectedServer] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickRange, setQuickRange] = useState("week");

  const load = useCallback(
    (from, to) => {
      if (!selectedServer || !from || !to) return;
      groupsStore.fetchGroups(selectedServer, from, to);
    },
    [selectedServer],
  );

  useEffect(() => {
    const init = async () => {
      const userId = localStorage.getItem("userId");
      if (userId) await settingsStore.getServers(userId);
      const today = new Date();
      const past = new Date(today);
      past.setDate(today.getDate() - 7);
      setFromDate(formatYmd(past));
      setToDate(formatYmd(today));
      logReportView({
        event: "report_view",
        reportKey: "groups",
        reportTitle: "Группы ресторанов",
        userId,
        path: "/groups",
      });
    };
    init();
  }, []);

  useEffect(() => {
    if (!settingsStore.servers?.length) return;
    if (!selectedServer) setSelectedServer(String(settingsStore.servers[0].id));
  }, [settingsStore.servers, selectedServer]);

  useEffect(() => {
    if (!selectedServer || !fromDate || !toDate) return;
    load(fromDate, toDate);
  }, [selectedServer, fromDate, toDate, load]);

  const top5 = groupsStore.groups.slice(0, 5);

  const openGroupOnDashboard = (groupName) => {
    navigate("/dashboard", {
      state: { serverId: selectedServer, restaurantGroup: groupName, groupByRestaurant: true },
    });
  };

  return (
    <CContainer style={{ flex: "1", marginTop: "120px" }}>
      <h4 className="mb-3">Сводка по группам ресторанов</h4>
      <ServerSelect
        servers={settingsStore.servers}
        value={selectedServer}
        onChange={(e) => setSelectedServer(e.target.value)}
      />
      <PeriodFilters
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
        onRefresh={load}
        quickRange={quickRange}
        onQuickRangeChange={setQuickRange}
        disabled={groupsStore.isLoading}
      />
      {groupsStore.errorMessage && (
        <CAlert color="danger" className="mt-3">
          {groupsStore.errorMessage}
        </CAlert>
      )}
      {groupsStore.isLoading && (
        <div className="text-center py-4">
          <CSpinner />
        </div>
      )}
      {!groupsStore.isLoading && (
        <>
          {top5.length > 0 && (
            <CCard className="mt-4 mb-4">
              <CCardBody>
                <CChartBar
                  data={{
                    labels: top5.map((g) => g.group),
                    datasets: [
                      {
                        label: "Выручка",
                        backgroundColor: "#7982f8",
                        data: top5.map((g) => g.revenue),
                      },
                    ],
                  }}
                />
              </CCardBody>
            </CCard>
          )}
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                <CTableHeaderCell>Группа</CTableHeaderCell>
                <CTableHeaderCell>Выручка</CTableHeaderCell>
                <CTableHeaderCell>Гости</CTableHeaderCell>
                <CTableHeaderCell>Чеки</CTableHeaderCell>
                <CTableHeaderCell>Ср. чек</CTableHeaderCell>
                <CTableHeaderCell>Доля</CTableHeaderCell>
                <CTableHeaderCell />
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {groupsStore.groups.map((g) => (
                <CTableRow key={g.group}>
                  <CTableDataCell>{g.group}</CTableDataCell>
                  <CTableDataCell>{fmtInt(g.revenue)} ₽</CTableDataCell>
                  <CTableDataCell>{fmtInt(g.guests)}</CTableDataCell>
                  <CTableDataCell>{fmtInt(g.orders)}</CTableDataCell>
                  <CTableDataCell>{fmtInt(g.avgCheck)} ₽</CTableDataCell>
                  <CTableDataCell style={{ minWidth: 140 }}>
                    <CProgress value={g.sharePct}>
                      <CProgressBar>{g.sharePct.toFixed(1)}%</CProgressBar>
                    </CProgress>
                  </CTableDataCell>
                  <CTableDataCell>
                    <CButton
                      size="sm"
                      color="primary"
                      variant="outline"
                      onClick={() => openGroupOnDashboard(g.group)}
                    >
                      На дашборд
                    </CButton>
                  </CTableDataCell>
                </CTableRow>
              ))}
            </CTableBody>
          </CTable>
        </>
      )}
    </CContainer>
  );
});

export default GroupsPage;
